export type IntegrationMode = 'lite' | 'minimal' | 'full'

export const INTEGRATION_QUERY_KEY = 'integration'
export const INTEGRATION_STORAGE_KEY = 'fe_integration_mode'
export const LITE_DRAW_PAGE_LIMIT = 30
export const LITE_RESULTS_PAGE_LIMIT = 10

const VALID_MODES = new Set<IntegrationMode>(['lite', 'minimal', 'full'])

export function normalizeIntegrationMode(value: unknown): IntegrationMode | null {
  const raw = Array.isArray(value) ? value[0] : value
  if (typeof raw !== 'string') return null
  const normalized = raw.trim().toLowerCase()
  if (!normalized || !VALID_MODES.has(normalized as IntegrationMode)) {
    return null
  }
  return normalized as IntegrationMode
}

export function readIntegrationFromSearch(search: string): IntegrationMode | null {
  try {
    const fromSearch = normalizeIntegrationMode(new URLSearchParams(search).get(INTEGRATION_QUERY_KEY))
    if (fromSearch) return fromSearch

    if (typeof window === 'undefined') return null

    const hash = window.location.hash
    const queryIndex = hash.indexOf('?')
    if (queryIndex === -1) return null

    return normalizeIntegrationMode(
      new URLSearchParams(hash.slice(queryIndex + 1)).get(INTEGRATION_QUERY_KEY)
    )
  } catch {
    return null
  }
}

export function hasIntegrationQueryInLocation(): boolean {
  if (typeof window === 'undefined') return false
  try {
    const search = new URLSearchParams(window.location.search)
    if (search.has(INTEGRATION_QUERY_KEY)) return true

    const hash = window.location.hash
    const queryIndex = hash.indexOf('?')
    if (queryIndex === -1) return false

    return new URLSearchParams(hash.slice(queryIndex + 1)).has(INTEGRATION_QUERY_KEY)
  } catch {
    return false
  }
}

export function getPersistedIntegrationMode(): IntegrationMode | null {
  if (typeof sessionStorage === 'undefined') return null
  const persisted = normalizeIntegrationMode(sessionStorage.getItem(INTEGRATION_STORAGE_KEY))
  return persisted === 'lite' ? 'lite' : null
}

export function persistIntegrationMode(mode: IntegrationMode | null): void {
  if (typeof sessionStorage === 'undefined') return
  if (mode === 'lite') {
    sessionStorage.setItem(INTEGRATION_STORAGE_KEY, mode)
  } else {
    sessionStorage.removeItem(INTEGRATION_STORAGE_KEY)
  }
}

/**
 * Only `integration=lite` enables the reduced UI. `minimal`, `full`, omitted, or unknown → full app.
 */
export function resolveIntegrationMode(_queryIntegration?: unknown): IntegrationMode | null {
  if (typeof window === 'undefined') {
    return normalizeIntegrationMode(_queryIntegration) === 'lite' ? 'lite' : null
  }

  if (!hasIntegrationQueryInLocation()) {
    persistIntegrationMode(null)
    return null
  }

  const fromUrl = readIntegrationFromSearch(window.location.search)
  if (fromUrl === 'lite') {
    persistIntegrationMode('lite')
    return 'lite'
  }

  persistIntegrationMode(null)
  return null
}

/** Reduced UI only for explicit `integration=lite`. */
export function isLiteIntegration(mode: IntegrationMode | null | undefined): boolean {
  return mode === 'lite'
}

/** Non-reactive check for boot-time code (main.ts, stores, api interceptors). */
export function isActiveLiteIntegration(): boolean {
  return isLiteIntegration(resolveIntegrationMode())
}
